import gql from 'graphql-tag';

import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {TRIAL_SAMPLE_SCHEMA_INTERFACE_ID} from 'registry/trial-interfaces/TrialInterfaceConstants';

import {TagSchemaCreatableEntityTypeFragment as TagSchemaCreatableEntityTypeFragmentType} from './__generated/TagSchemaCreatableEntityTypeFragment';
import {AdditionalCreatableEntityType, CreatableEntityType} from './EntityCreationConfigTypes';

/**
 * Converts a TagSchemaItemType and schema interface IDs to a CreatableEntityType.
 *
 * This function checks for special cases where the schema interface IDs indicate a special entity creation
 * flow that should use a different CreatableEntityType instead of the base TagSchemaItemType.
 *
 * @param tagSchemaItemType The base tag schema item type
 * @param schemaInterfaceIds Array of schema interface IDs to check for special cases
 * @returns The appropriate CreatableEntityType
 */
function getCreatableEntityType(
  tagSchemaItemType: TagSchemaItemType,
  schemaInterfaceIds: Array<string>
): CreatableEntityType {
  // Check if this is a trial sample schema (uses special creation flow)
  const isTrialSampleSchema = schemaInterfaceIds.includes(TRIAL_SAMPLE_SCHEMA_INTERFACE_ID);
  if (isTrialSampleSchema) {
    return AdditionalCreatableEntityType.TRIAL_SAMPLE;
  }

  // Default to the original TagSchemaItemType
  return tagSchemaItemType;
}

const TAG_SCHEMA_CREATABLE_ENTITY_TYPE_FRAGMENT = gql`
  fragment TagSchemaCreatableEntityTypeFragment on TagSchemaNode {
    id
    itemType
    interfaceConfigs {
      interface {
        id
      }
    }
  }
`;

export const TagSchemaCreatableEntityTypeFragment: FragmentWrapper<TagSchemaCreatableEntityTypeFragmentType> =
  {
    fragment: TAG_SCHEMA_CREATABLE_ENTITY_TYPE_FRAGMENT,
    fragmentName: 'TagSchemaCreatableEntityTypeFragment',
  };

/**
 * Converts a TagSchema fragment to a CreatableEntityType.
 *
 * The best way to use this function is to add the TagSchemaCreatableEntityTypeFragment GraphQL fragment
 * to your component's existing GraphQL query (whether you are getting one schema or listing all schemas)
 * and then call this function with the queried tagSchema.
 *
 * @param tagSchema An instance of TagSchemaCreatableEntityTypeFragment
 * @returns The appropriate CreatableEntityType
 */
export function tagSchemaToCreatableEntityType(
  tagSchema: TagSchemaCreatableEntityTypeFragmentType
): CreatableEntityType {
  const schemaInterfaceIds = tagSchema.interfaceConfigs.map((c) => c.interface.id);
  return getCreatableEntityType(tagSchema.itemType, schemaInterfaceIds);
}
